<script setup lang="ts">
  import router from '@/router'
  import dotsIcon from '@assets/icons/dots.svg'
  import viewChatIcon from '@assets/icons/edit-chat.svg'
  import trashIcon from '@assets/icons/trash.svg'
  import type { Chat } from '@/types'
  import { useDisplay } from 'vuetify/framework'

  const iconStyles = computed(() => ({
    '--icon-stroke': 'rgb(var(--v-theme-grey-300))',
    '--icon-fill': 'none',
  }))

  const editChatDialog = ref<boolean>(false)
  const selectedChat = ref<Chat | null>(null)
  const editChatTitle = ref<string | null>('')
  const editChatLoading = ref<boolean>(false)
  const chatMenus = ref<Record<string, boolean>>({})

  const chatStore = useChatStore()
  const { t } = useI18n()
  const { mobile } = useDisplay()

  const { chats } = defineProps<{
    chats: Chat[],
  }>()

  const saveChatTitle = async () => {
    if (!selectedChat.value || !editChatTitle?.value?.trim()) return

    editChatLoading.value = true
    const oldTitle = selectedChat.value.title

    try {
      selectedChat.value.title = editChatTitle.value.trim()
      await chatStore.updateChatTitle(selectedChat.value.id, editChatTitle.value.trim())
      closeEditChatDialog()
    } catch (error) {
      if (selectedChat.value) {
        selectedChat.value.title = oldTitle
      }
      console.error('Failed to update chat title:', error)
    } finally {
      editChatLoading.value = false
    }
  }

  const closeEditChatDialog = () => {
    editChatDialog.value = false
    selectedChat.value = null
    editChatTitle.value = ''
    editChatLoading.value = false
  }

  const openEditChatDialog = (chat: Chat) => {
    selectedChat.value = chat
    editChatTitle.value = chat.title
    editChatDialog.value = true
  }

</script>

<template>

  <v-list class="pt-0">
    <!-- <transition-group name="list-swap"> -->
    <v-list-item
      v-for="chat in chats"
      :key="chat.id"
      :active="false"
      class="chat-list-item pa-0 mb-1"
      :class="{
        'desktop-show-on-hover': !mobile
      }"
      density="compact"
      rounded="lg"
      @click.stop="router.push('/c/' + chat.id)"
    >
      <v-list-item-title
        class="chat-title ps-3 pe-2 text-subtitle-2"
      >
        {{ chat.title || t('untitled_chat') }}
      </v-list-item-title>

      <template #append>
        <v-menu v-model="chatMenus[chat.id]" location="start" width="180">
          <template #activator="{ props }">
            <v-icon
              v-bind="props"
              class="mx-1"
              elevation="0"
              :icon="dotsIcon"
              size="20"
              start
              :style="iconStyles"
              variant="text"
            />
          </template>
          <v-card class="pa-2 rounded-lg bg-grey-950 border-sm border-opacity-100 border-grey-800">
            <v-btn
              :append-icon="viewChatIcon"
              block
              class="py-1 ps-2 pe-3 justify-space-between rounded-sm text-subtitle-2"
              density="compact"
              elevation="0"
              size="large"
              variant="text"
              @click.stop="openEditChatDialog(chat); chatMenus[chat.id] = false"
            >
              {{ t('rename_chat') }}
            </v-btn>
            <v-divider color="grey-800 my-2" />
            <v-btn
              :append-icon="trashIcon"
              block
              class="py-1 ps-2 pe-3 justify-space-between rounded-sm text-subtitle-2"
              color="red"
              density="compact"
              elevation="0"
              size="large"
              variant="text"
              @click.stop="chatStore.deleteChat(chat.id); chatMenus[chat.id] = false"
            >
              {{ t('delete_chat') }}
            </v-btn>
          </v-card>
        </v-menu>
      </template>
    </v-list-item>
    <!-- </transition-group> -->
  </v-list>
  <app-dialog
    v-model="editChatDialog"
    max-width="400"
    :show-close-button="false"
  >
    <div class="d-flex flex-column ga-4">
      <div class="text-center">
        <h3 class="text-grey-100 font-weight-bold">
          <i18n-t keypath="edit_chat" />
        </h3>
        <p class="text-grey-200">
          {{ t('edit_chat_description') }}
        </p>
      </div>

      <v-text-field
        v-model="editChatTitle"
        density="comfortable"
        :label="t('chat_title')"
        :rules="[v => !!v || t('chat_title_required')]"
        variant="outlined"
        @keyup.enter="saveChatTitle"
      />

      <div class="d-flex flex-column ga-3">
        <v-btn
          block
          class="font-weight-bold"
          color="primary"
          density="default"
          :disabled="!editChatTitle?.trim()"
          flat
          :loading="editChatLoading"
          size="large"
          @click="saveChatTitle"
        >
          <i18n-t keypath="save" />
        </v-btn>
        <v-btn
          class="font-weight-bold"
          :disabled="editChatLoading"
          flat
          size="large"
          variant="outlined"
          @click="closeEditChatDialog"
        >
          <i18n-t keypath="cancel" />
        </v-btn>
      </div>
    </div>
  </app-dialog>
</template>

<style lang="scss" scoped>
.chat-drawer {
  .chat-drawer-content {
    .chat-list-item {
      padding-inline: unset;

      ::v-deep(.v-list-item__append) {
        width: 32px !important;
        min-width: 32px !important;
      }

      .chat-title {
        width: 100%;
        flex-grow: 1;
        font-size: 0.95rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .empty-state {
      height: calc(100% - 60px);
      opacity: 0.7;
    }
  }
}

.list-swap-move {
  transition: transform 0.5s ease;
}

.list-swap-leave-active {
  position: absolute;
}

.desktop-show-on-hover {
  :deep(.v-list-item__append) {
    display: none;
  }

  &:hover {
    :deep(.v-list-item__append) {
      display: block;
    }
  }
}
</style>
